import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, RefreshCw, FileText } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

// Modal de "Ver documento": muestra la preview del archivo (PDF embebido o imagen)
// junto a los datos que leyó el OCR. El componente renderiza su propio botón
// disparador 👁 y pide el archivo al backend al abrirse (reutiliza /download via
// blob; no hace falta endpoint nuevo). Solo lectura.

// Campos del OCR que sabemos mostrar, en orden, con su etiqueta legible.
const OCR_FIELDS = [
  ["supplier_name", "Proveedor"],
  ["supplier_nif", "NIF/CIF"],
  ["nif", "NIF/CIF"],
  ["client_name", "Cliente"],
  ["client_nif", "NIF cliente"],
  ["supplier_type", "Tipo"],
  ["series", "Serie"],
  ["invoice_number", "Nº factura"],
  ["invoice_date", "Fecha"],
  ["due_date", "Vencimiento"],
  ["concept", "Concepto"],
  ["base_amount", "Base imponible"],
  ["vat_rate", "IVA %"],
  ["vat_amount", "IVA"],
  ["irpf_rate", "IRPF %"],
  ["irpf_amount", "IRPF"],
  ["total_amount", "Total"],
  ["currency", "Divisa"],
];

const IMAGE_TYPES = ["png", "jpg", "jpeg", "gif", "bmp"];

function fmtValue(v) {
  if (typeof v === "number") return new Intl.NumberFormat("es-ES").format(v);
  return String(v);
}

function OcrFields({ ocr }) {
  const status = ocr?.status;
  if (!ocr || status === "no_data") {
    return (
      <p className="text-sm text-slate-500">
        No se extrajeron datos de este documento.
      </p>
    );
  }
  if (status === "unsupported") {
    return (
      <p className="text-sm text-slate-500">
        {ocr.error || "Formato no soportado por el OCR."}
      </p>
    );
  }
  const seen = new Set();
  const rows = [];
  for (const [key, label] of OCR_FIELDS) {
    if (seen.has(label)) continue;
    const v = ocr[key];
    if (v === null || v === undefined || v === "") continue;
    seen.add(label);
    rows.push([label, v]);
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No se extrajeron datos de este documento.
      </p>
    );
  }
  return (
    <dl className="divide-y divide-slate-100">
      {rows.map(([label, v]) => (
        <div key={label} className="flex justify-between gap-4 py-1.5 text-sm">
          <dt className="text-slate-500">{label}</dt>
          <dd className="font-medium text-slate-900 text-right">{fmtValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function DocumentViewerDialog({ doc }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const fileType = (doc.file_type || "").toLowerCase();
  const isImage = IMAGE_TYPES.includes(fileType);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiFetch(`${API}/documents/${doc.document_id}/download`);
      if (!res.ok) {
        setError(true);
        return;
      }
      const blob = await res.blob();
      setBlobUrl(URL.createObjectURL(blob));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const cleanup = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) load();
    else cleanup();
  };

  const handleTrigger = (e) => {
    e.stopPropagation();
    handleOpenChange(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleTrigger}
        title="Ver documento"
        className="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-colors"
        data-testid={`view-doc-${doc.document_id}`}
      >
        <Eye className="w-4 h-4" />
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="truncate">{doc.filename}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Preview */}
            <div className="md:col-span-3 min-h-[60vh] bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden">
              {loading ? (
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              ) : error ? (
                <p className="text-sm text-rose-600 p-4">
                  No se pudo cargar la vista previa del documento.
                </p>
              ) : !blobUrl ? null : isImage ? (
                <img
                  src={blobUrl}
                  alt={doc.filename}
                  className="max-h-[70vh] w-full object-contain"
                />
              ) : (
                <iframe
                  src={blobUrl}
                  title={doc.filename}
                  className="w-full h-[70vh]"
                />
              )}
            </div>

            {/* Datos OCR */}
            <div className="md:col-span-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Datos leídos por el OCR
              </h3>
              <OcrFields ocr={doc.ocr_result} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
